import { PayloadAction, createSlice } from '@reduxjs/toolkit';
// Import the specific action module, not the `devices/state` barrel: the barrel pulls
// in the devices slice + telemetry listener, which transitively re-import user/state —
// a circular dependency that leaves the thunk's action creators undefined at load.
import { mediaSettingsActions } from 'devices/state/mediaSettings.actions';
import { type MediaSettingsState, MediaSettingsTab } from 'devices/types';
import { natalieActions } from 'pages/Natalie/state/natalie.actions';
import { agoraActions } from 'store/features/agora/agora.actions';

import { userActions } from './actions';
import { UserState } from './types';

import type { BlurLevel } from 'webRTC/types';

const getBlurLevel = (): BlurLevel => {
  try {
    const blurLevelValue = globalThis.localStorage?.getItem?.('blurLevel');
    return Number(blurLevelValue ?? 0) as BlurLevel;
  } catch {
    return 0 as BlurLevel;
  }
};

// Not validated here: this module can't know which background ids exist. An id that no longer
// resolves is treated as "no background" at the point it's applied.
const getBackgroundImageId = (): string => {
  try {
    return globalThis.localStorage?.getItem?.('backgroundImageId') ?? '';
  } catch {
    return '';
  }
};

export const initialUserState: UserState = {
  mediaDevicesLoaded: false,
  mediaDevices: [],
  mediaSettings: {
    audioInputDeviceId: '',
    audioOutputDeviceId: '',
    videoDeviceId: '',
    blurLevel: getBlurLevel(),
    backgroundImageId: getBackgroundImageId(),
  },
  userSettings: {
    isAudioEnabled: true,
    isVideoEnabled: true,
  },
  isMediaSettingsOpen: false,
  initialMediaSettingsTab: MediaSettingsTab.Video,
  microphoneIssueDetected: false,
  microphoneIssueAcknowledged: false,
  isOnline: true,
  showMemberSetupDialog: false,
};

export const userSlice = createSlice({
  name: 'user',
  initialState: initialUserState,
  reducers: {
    setAudioEnabled: (state, action: PayloadAction<boolean>) => {
      state.userSettings.isAudioEnabled = action.payload;
    },
    setVideoEnabled: (state, action: PayloadAction<boolean>) => {
      state.userSettings.isVideoEnabled = action.payload;
    },
    setMediaSettings: (state, action: PayloadAction<Partial<MediaSettingsState>>) => {
      state.mediaSettings = {
        ...state.mediaSettings,
        ...action.payload,
      };
    },
    setMediaSettingsIsOpen: (state, { payload }: PayloadAction<{ open: boolean; initialTab?: MediaSettingsTab }>) => {
      state.isMediaSettingsOpen = payload.open;
      state.initialMediaSettingsTab = payload.initialTab ?? MediaSettingsTab.Video;
    },
    setVideoDeviceId: (state, action: PayloadAction<string>) => {
      state.mediaSettings.videoDeviceId = action.payload;
    },
    setAudioInputDeviceId: (state, action: PayloadAction<string>) => {
      state.mediaSettings.audioInputDeviceId = action.payload;
    },
    setMicrophoneIssueDetected: (state, action: PayloadAction<boolean>) => {
      state.microphoneIssueDetected = action.payload;
    },
    setMicrophoneIssueAcknowledged: (state, action: PayloadAction<boolean>) => {
      state.microphoneIssueAcknowledged = action.payload;
    },
    setIsOnline: (state, action: PayloadAction<boolean>) => {
      state.isOnline = action.payload;
    },
    setAudioDisabled: (state) => {
      state.userSettings.isAudioEnabled = false;
    },
    setShowMemberSetupDialog: (state, action) => {
      state.showMemberSetupDialog = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(userActions.navigator.mediaDevices.devicesFound, (state, action) => {
        state.mediaDevices = action.payload;
      })
      .addCase(userActions.navigator.mediaDevices.devicesChanged.fulfilled, (state) => {
        state.mediaDevicesLoaded = true;
      })
      .addCase(userActions.mediaSettings.settingsUpdated, (state, action) => {
        const {
          audioInputDeviceId,
          audioOutputDeviceId,
          backgroundImageId,
          blurLevel,
          isAudioEnabled,
          isVideoEnabled,
          videoDeviceId,
        } = action.payload;

        state.mediaSettings = {
          audioInputDeviceId,
          audioOutputDeviceId,
          backgroundImageId,
          blurLevel,
          videoDeviceId,
        };

        state.userSettings = {
          isAudioEnabled,
          isVideoEnabled,
        };
      })
      .addCase(userActions.mediaSettings.videoBackgroundShed, (state) => {
        state.mediaSettings.blurLevel = 0;
        state.mediaSettings.backgroundImageId = '';
      })
      .addCase(agoraActions.audioTrack.toggling.failed, (state) => {
        state.userSettings.isAudioEnabled = !state.userSettings.isAudioEnabled;
      })
      .addCase(agoraActions.videoTrack.toggling.failed, (state) => {
        state.userSettings.isVideoEnabled = !state.userSettings.isVideoEnabled;
      })
      .addCase(mediaSettingsActions.toggleMedia.openAudioSettingsClicked, (state) => {
        state.isMediaSettingsOpen = true;
        state.initialMediaSettingsTab = MediaSettingsTab.Audio;
      })
      .addCase(mediaSettingsActions.toggleMedia.openVideoSettingsClicked, (state) => {
        state.isMediaSettingsOpen = true;
        state.initialMediaSettingsTab = MediaSettingsTab.Video;
      })
      .addCase(
        natalieActions.joinPage.joinButton.clicked,
        (state, { payload: { isMicrophoneEnabled, microphoneId, speakerId } }) => {
          state.mediaSettings.audioInputDeviceId = microphoneId;
          state.mediaSettings.audioOutputDeviceId = speakerId;
          state.userSettings.isAudioEnabled = isMicrophoneEnabled;
        }
      );
  },
});
